import { resolveCartSelectionIdentity } from '@/utils/cart-selection-identity';
import { createDefaultAutoOfferStore, hasExplicitAutoOfferSelection } from './index';
import type { AutoOfferStore } from './types';

/** 只保存用户意图；不保存实验、账户凭证、金额和已应用结果。 */
export type CartOfferIntent = Pick<
  AutoOfferStore,
  'couponMode' | 'pointMode' | 'coupon' | 'code' | 'expendPoint'
>;

export function getCartOfferScope(input: {
  site: string;
  authenticated: boolean;
  userId?: number | string | null;
  token?: string | null;
  tokenReady: boolean;
  userReady: boolean;
}): string | null {
  // userReady 表示账户初始化已结束，不代表资料请求成功；结束前禁止使用上个账户的缓存。
  if (!input.tokenReady || (input.authenticated && !input.userReady)) return null;
  // userId 只用于优惠缓存隔离，计价接口仍凭 token 鉴权；资料失败不能持续阻断计价。
  // 缺少 userId 时按当前 token 的摘要隔离临时选择，不保存原始凭证。
  const identity = input.authenticated
    ? input.userId
      ? `user:${input.userId}`
      : resolveCartSelectionIdentity({ authenticated: true, token: input.token })
    : 'guest';
  if (!identity) return null;
  // 沿用真实选择的存储键；不迁移已废弃的 preview 数据，避免模拟操作污染真实计价。
  return JSON.stringify([input.site, 'live', identity]);
}

function parseIntent(raw: unknown): CartOfferIntent | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const value = raw as Record<string, unknown>;
  if (!['auto', 'skip', 'manual_coupon', 'manual_code'].includes(String(value.couponMode)))
    return null;
  if (!['auto', 'none', 'manual'].includes(String(value.pointMode))) return null;
  if (
    value.couponMode === 'manual_coupon' &&
    (typeof value.coupon !== 'number' || !Number.isSafeInteger(value.coupon) || value.coupon <= 0)
  )
    return null;
  if (
    value.couponMode === 'manual_code' &&
    (typeof value.code !== 'string' || !value.code.trim() || value.code.length > 256)
  )
    return null;
  if (
    value.pointMode === 'manual' &&
    (typeof value.expendPoint !== 'number' ||
      !Number.isSafeInteger(value.expendPoint) ||
      value.expendPoint <= 0)
  )
    return null;
  return {
    couponMode: value.couponMode as CartOfferIntent['couponMode'],
    pointMode: value.pointMode as CartOfferIntent['pointMode'],
    coupon: value.couponMode === 'manual_coupon' ? (value.coupon as number) : undefined,
    code: value.couponMode === 'manual_code' ? (value.code as string).trim() : '',
    expendPoint: value.pointMode === 'manual' ? (value.expendPoint as number) : undefined,
  };
}

const storageKey = (scope: string) => `zl-cart-offer-intent:v1:${scope}`;

export function readCartOfferIntent(
  scope: string,
  sessionScope?: string | null,
): CartOfferIntent | null {
  // 资料恢复后只迁移当前 token 的临时选择；写入成功才移除，防止重进时被旧账户缓存覆盖。
  const sessionIntent =
    sessionScope && sessionScope !== scope ? readCartOfferIntent(sessionScope) : null;
  if (sessionIntent) {
    // 手动券/码、主动取消或积分选择优先沿用当前会话；会话只有默认值时保留账户历史选择。
    const intent = hasExplicitAutoOfferSelection({
      ...createDefaultAutoOfferStore(),
      ...sessionIntent,
    })
      ? sessionIntent
      : readCartOfferIntent(scope) || sessionIntent;
    try {
      localStorage.setItem(storageKey(scope), JSON.stringify({ version: 1, intent }));
      localStorage.removeItem(storageKey(sessionScope!));
    } catch {
      // 存储失败不影响当前计价，也保留临时记录供后续恢复。
    }
    return intent;
  }
  try {
    const raw = localStorage.getItem(storageKey(scope));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      !('version' in parsed) ||
      parsed.version !== 1 ||
      !('intent' in parsed)
    )
      return null;
    return parseIntent(parsed.intent);
  } catch {
    // 禁用存储或损坏缓存不能阻断当前页面操作。
    return null;
  }
}

export function writeCartOfferIntent(
  scope: string,
  selection: AutoOfferStore,
  sessionScope?: string | null,
): void {
  const intent = parseIntent(selection);
  if (!intent) return;
  try {
    localStorage.setItem(storageKey(scope), JSON.stringify({ version: 1, intent }));
    // 存储恢复后，新的正式选择取代未能迁移的临时记录，避免下次又恢复旧券/取消状态。
    if (sessionScope && sessionScope !== scope) localStorage.removeItem(storageKey(sessionScope));
  } catch {
    // 仅跨刷新恢复降级，不清除当前内存意图。
  }
}

/** 计价依赖只含输入，不含服务端结果，避免回写优惠结果触发请求循环。 */
export function cartOfferInputKey(selection: AutoOfferStore): string {
  return JSON.stringify([
    selection.enabled,
    selection.authenticated,
    selection.couponPolicy,
    selection.couponMode,
    selection.coupon,
    selection.code,
    selection.pointMode,
    selection.expendPoint,
  ]);
}
